import React, { useEffect, useState } from "react";
import { Card, List, Tag, Typography, Space, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { AirplaneOutlined } from "../../../../components/icons/SvgIcons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import WidgetWrapper from "../../components/WidgetWrapper";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { FLIGHT_STATUS_COLORS } from "../../../flightOperation/Monitor/constants/flightConstants";
import { createNavHelper } from "../../../../hooks/useUrlState";

dayjs.extend(utc);

const { Text } = Typography;

const AircraftStatusListWidget = () => {
    const navigate = useNavigate();
    const navHelper = createNavHelper(navigate);
    const [loading, setLoading] = useState(false);
    const [aircraftData, setAircraftData] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch flights for today and tomorrow to cover active and upcoming
            const start = dayjs().subtract(1, "day").format("YYYY-MM-DD");
            const end = dayjs().add(1, "day").format("YYYY-MM-DD");

            const res = await flightOperationFlightService.getFlightOperationsWithTransactions({
                start_date: start,
                end_date: end,
                page_size: 500,
                status: "SCHEDULED,DEPARTED,DELAYED,DIVERTED,COMPLETED", // Active statuses + COMPLETED
            });

            // Group by Aircraft and find the most relevant flight (Active > Upcoming > Recent)
            const grouped = {};
            const items = res.data?.items || [];

            items.forEach((flight) => {
                const reg = flight.aircraft_reg || "Unassigned";
                if (!grouped[reg]) grouped[reg] = [];
                grouped[reg].push(flight);
            });

            const processedData = Object.keys(grouped)
                .map((reg) => {
                    const flights = grouped[reg];
                    // Sort by STD
                    flights.sort((a, b) => dayjs(a.std).valueOf() - dayjs(b.std).valueOf());

                    // Find "Current" flight: Departed but not Arrived, or next Scheduled
                    let current = flights.find((f) => f.status === "DEPARTED" || f.status === "DELAYED" || f.status === "DIVERTED");
                    if (!current) {
                        // If no active flight, find next scheduled
                        current = flights.find((f) => f.status === "SCHEDULED" && dayjs(f.std).isAfter(dayjs()));
                    }
                    if (!current) {
                        // If no future scheduled, take the last one
                        current = flights[flights.length - 1];
                    }

                    return {
                        reg,
                        flight: current,
                    };
                })
                .filter((item) => item.reg !== "Unassigned"); // Filter out unassigned for this view

            setAircraftData(processedData);
        } catch (error) {
            console.error("Failed to fetch aircraft status", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Helper to render transaction status
    const renderTransactionStatus = (transactions, type, label) => {
        const list = transactions?.[type] || [];
        // Assuming list is sorted by date desc in backend or we take the latest
        const latest = list.length > 0 ? list[0] : null;

        let icon = <ClockCircleOutlined style={{ color: "var(--text-muted)" }} />; // Default grey
        let color = "default";
        let statusText = "N/A";

        if (latest) {
            statusText = latest.status;
            if (latest.status === "APPROVED" || latest.status === "CONFIRMED") {
                icon = <CheckCircleOutlined style={{ color: "var(--status-success)" }} />;
                color = "success";
            } else if (latest.status === "PENDING" || latest.status === "PENDING_REVIEW") {
                icon = <SyncOutlined spin style={{ color: "var(--status-info)" }} />;
                color = "processing";
            } else if (latest.status === "REJECTED" || latest.status === "CANCELLED") {
                icon = <CloseCircleOutlined style={{ color: "var(--status-error)" }} />;
                color = "error";
            }
        }

        return (
            <Tooltip title={`${label}: ${statusText}`}>
                <Space size={2} style={{ marginRight: 8, fontSize: 12 }}>
                    {icon}{" "}
                    <Text type="secondary" style={{ fontSize: 10, color: "var(--text-secondary)" }}>
                        {label}
                    </Text>
                </Space>
            </Tooltip>
        );
    };

    // Helper component for hover effects
    const InteractableCard = ({ children, style, ...props }) => {
        const [hover, setHover] = React.useState(false);
        return (
            <Card
                style={{
                    ...style,
                    transition: "all 0.3s",
                    border: hover ? "1px solid #1890ff" : "1px solid var(--border-color, #f0f0f0)", // Blue on hover
                    transform: hover ? "translateY(-2px)" : "none",
                    boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                {...props}
            >
                {children}
            </Card>
        );
    };

    return (
        <WidgetWrapper title="机队状态监控" loading={loading} onRefresh={fetchData} bordered={false}>
            <List
                grid={{ gutter: 14, column: 1 }}
                dataSource={aircraftData}
                renderItem={(item) => {
                    const { reg, flight } = item;
                    if (!flight) return null;

                    const isAirborne = flight.status === "DEPARTED";
                    const progress = isAirborne ? 60 : 0; // Mock progress for visual

                    // 点击卡片时跳转到航班监控页面，并筛选该飞机
                    const handleCardClick = () => {
                        navHelper.toFlightMonitor({
                            registrations: [reg],
                        });
                    };

                    return (
                        <List.Item>
                            <InteractableCard
                                size="small"
                                onClick={handleCardClick}
                                style={{
                                    background: "var(--widget-header-bg)", // Slightly lighter than widget bg
                                    borderRadius: 12,
                                    overflow: "hidden", // Fix border radius clipping
                                    marginBottom: 12, // Add vertical spacing between cards
                                    cursor: "pointer", // Indicate clickable
                                }}
                            >
                                {/* Header: Reg + Flight No */}
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                    <Space>
                                        <Text strong style={{ color: "var(--text-primary)", fontSize: 16 }}>
                                            {reg}
                                        </Text>
                                        <Tag color="blue" style={{ border: "none", background: "rgba(24, 144, 255, 0.2)", color: "#1890ff" }}>
                                            {flight.flight_number}
                                        </Tag>
                                    </Space>
                                    <Tag color={FLIGHT_STATUS_COLORS[flight.status] || "default"}>{flight.status}</Tag>
                                </div>

                                {/* Route & Time */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ textAlign: "left" }}>
                                        <div style={{ color: "var(--text-primary)", fontSize: 18, fontWeight: "bold" }}>{flight.departure_airport}</div>
                                        <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>{dayjs.utc(flight.std).format("D-MMM HH:mm")} UTC</div>
                                    </div>

                                    {/* Visual Route Line */}
                                    <div style={{ flex: 1, margin: "0 16px", position: "relative", height: 20 }}>
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: 0,
                                                right: 0,
                                                height: 2,
                                                background: "rgba(255,255,255,0.1)",
                                                marginTop: -1,
                                            }}
                                        />
                                        {isAirborne && (
                                            <AirplaneOutlined
                                                style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: `${progress}%`,
                                                    transform: "translate(-50%, -50%)",
                                                    color: "var(--accent-primary)",
                                                    fontSize: 16,
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ color: "var(--text-primary)", fontSize: 18, fontWeight: "bold" }}>{flight.arrival_airport}</div>
                                        <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>{dayjs.utc(flight.sta).format("D-MMM HH:mm")} UTC</div>
                                    </div>
                                </div>

                                {/* Transactions Footer */}
                                <div
                                    style={{
                                        borderTop: "1px solid var(--border-color)",
                                        paddingTop: 8,
                                        display: "flex",
                                        justifyContent: "flex-start",
                                    }}
                                >
                                    {renderTransactionStatus(flight.transactions, "permit_requests", "许可")}
                                    {renderTransactionStatus(flight.transactions, "fuel_requests", "燃油")}
                                    {renderTransactionStatus(flight.transactions, "catering_orders", "配餐")}
                                    {renderTransactionStatus(flight.transactions, "crew_transportations", "车辆")}
                                </div>
                            </InteractableCard>
                        </List.Item>
                    );
                }}
            />
        </WidgetWrapper>
    );
};

export default AircraftStatusListWidget;
